import React, { cloneElement, isValidElement, useCallback, useContext, useMemo, useReducer } from "react";

//#region src/components.tsx
const TranslationsContext = React.createContext(void 0);
/**
* Provides a translation context for your application
*
* @example
* import { VocabProvider } from '@vocab/react';
*
* <VocabProvider language="en">
*   <App />
* <VocabProvider />
*/
const VocabProvider = ({ children, language, locale }) => {
	const value = useMemo(() => ({
		language,
		locale
	}), [language, locale]);
	return /* @__PURE__ */ React.createElement(TranslationsContext.Provider, { value }, children);
};
/**
* @returns The `language` and `locale` values passed in to your `VocabProvider`
*/
const useLanguage = () => {
	const context = useContext(TranslationsContext);
	if (!context) throw new Error("Attempted to access translation without Vocab context set. Did you forget to render VocabProvider?");
	if (!context.language) throw new Error("Attempted to access translation without language set. Did you forget to pass language to VocabProvider?");
	return context;
};
const SERVER_RENDERING = typeof window === "undefined";
function useTranslations(translations) {
	const { language, locale } = useLanguage();
	const [, forceRender] = useReducer((s) => s + 1, 0);
	const translationsObject = translations.getLoadedMessages(language, locale || language);
	let ready = true;
	if (!translationsObject) {
		if (SERVER_RENDERING) throw new Error(`Translations not synchronously available on server render. Applying translations dynamically server-side is not supported.`);
		translations.load(language).then(() => {
			forceRender();
		});
		ready = false;
	}
	const t = useCallback((key, params) => {
		if (!translationsObject) return " ";
		const message = translationsObject?.[key];
		if (!message) {
			console.error(`Unable to find translation for key "${key}". Possible keys are ${Object.keys(translationsObject).map((v) => `"${v}"`).join(", ")}`);
			return "";
		}
		const result = message.format(params);
		if (Array.isArray(result)) for (let i = 0; i < result.length; i++) {
			const item = result[i];
			if (typeof item === "object" && item && !item.key && isValidElement(item)) result[i] = cloneElement(item, { key: `_vocab-${i}` });
		}
		return result;
	}, [translationsObject]);
	return {
		ready,
		t
	};
}

//#endregion
export { VocabProvider, useLanguage, useTranslations };
//# sourceMappingURL=index.mjs.map